<template>
  <div v-if="visible" class="card border-0 shadow-sm rounded-3 mb-3">
    <div class="card-body p-3">
      <div class="d-flex align-items-center gap-2 mb-2">
        <div class="promo-icon flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center">
          <i class="fa-solid fa-gift" />
        </div>
        <div class="flex-grow-1 min-w-0">
          <div class="fw-bold text-truncate" style="font-size: 0.9rem;">{{ promotionName || $t('_promotion.Promotion') }}</div>
          <div class="text-muted" style="font-size: 0.7rem;">
            <i class="fa-solid fa-layer-group me-1" />{{ tierMode === 'single' ? $t('_promotion.Pick any one tier') : $t('_promotion.All reached tiers') }}
          </div>
        </div>
        <span v-if="loading" class="flex-shrink-0"><i class="fa-solid fa-spinner fa-spin text-muted" /></span>
      </div>

      <!-- single: ใช้สิทธิ์ไปแล้ว (เลือกได้ 1 อย่าง/บิล) -->
      <div v-if="promoUsed" class="promo-used d-flex align-items-center gap-2 rounded-3 px-3 py-2 mb-2" style="font-size: 0.78rem;">
        <i class="fa-solid fa-circle-check" />{{ $t('_promotion.Promotion used') }}
      </div>

      <!-- Tier cards (progress นับรวมตะกร้า) -->
      <div
        v-for="tier in visibleTiers"
        :key="tier.promotion_item_id"
        class="promo-tier rounded-3 px-2 py-2 mb-2"
        :class="{ 'promo-tier--met': reachedByCart(tier) || isClaimedServer(tier) }"
      >
        <div class="d-flex justify-content-between align-items-center gap-2">
          <span class="min-w-0" style="font-size: 0.82rem;">
            <i
              class="me-1"
              :class="(reachedByCart(tier) || isClaimedServer(tier)) ? 'fa-solid fa-circle-check text-success' : 'fa-regular fa-circle text-muted'"
            />
            <span class="text-muted" style="font-size: 0.72rem;">{{ $t('_promotion.Reach') }} {{ thresholdLabel(tier) }} ·</span>
            <span class="fw-semibold">
              {{ $ln(tier, 'reward_product_name') }}<span v-if="tier.free_quantity > 1"> × {{ tier.free_quantity }}</span>
            </span>
            <span v-if="tier.is_pwp" class="badge bg-info-subtle text-info ms-1">{{ $storeIndex.formatThaiCurrency(tier.reward_price) }}</span>
            <span v-else class="badge bg-success-subtle text-success ms-1">{{ $t('_promotion.Free') }}</span>
            <span v-if="isClaimedServer(tier)" class="badge bg-success-subtle text-success ms-1">
              <i class="fa-solid fa-check me-1" />{{ $t('_promotion.claimed') }}
            </span>
            <span v-else-if="isPending(tier)" class="badge bg-success-subtle text-success ms-1">
              <i class="fa-solid fa-cart-shopping me-1" />{{ $t('_promotion.In cart') }}
            </span>
            <span v-if="remainingFor(tier) > 0 && showStockEarly && !tier.reward_in_stock" class="badge bg-secondary ms-1">{{ $t('_promotion.Currently out of stock') }}</span>
          </span>

          <!-- วัตถุดิบหมด → ปิดปุ่ม -->
          <span
            v-if="reachedByCart(tier) && !isClaimedServer(tier) && !tier.reward_in_stock"
            class="badge bg-secondary flex-shrink-0"
          >
            {{ $t('_promotion.Out of stock') }}
          </span>
          <!-- ถึงแล้ว (จากตะกร้า) + ยังไม่ได้ใส่ + มีของ → ปุ่มรับ (ใส่ลงตะกร้า) -->
          <button
            v-else-if="canClaim(tier)"
            type="button"
            class="btn btn-sm rounded-pill px-3 flex-shrink-0 fw-semibold"
            :class="tier.is_pwp ? 'btn-info text-white' : 'btn-success text-white'"
            :disabled="busy"
            @click="onClaimClick(tier)"
          >
            <i class="fa-solid fa-cart-plus me-1" />{{ tier.is_pwp ? $storeIndex.formatThaiCurrency(tier.reward_price) : $t('_promotion.Claim now') }}
          </button>
        </div>

        <!-- ขั้นที่ยังไม่ถึง → progress bar สดตามตะกร้า -->
        <template v-if="remainingFor(tier) > 0 && !isClaimedServer(tier)">
          <div class="progress mt-2" style="height: 6px;">
            <div class="progress-bar bg-success" :style="{ width: tierProgress(tier) + '%' }" />
          </div>
          <div class="d-flex justify-content-between align-items-center mt-1" style="font-size: 0.7rem;">
            <span class="text-muted">{{ baseLabel(tier) }} / {{ thresholdLabel(tier) }}</span>
            <span class="text-muted">
              {{ $t('_promotion.Buy more') }} <b class="text-theme">{{ remainingLabel(tier) }}</b>
            </span>
          </div>
        </template>
      </div>

      <!-- ของรางวัลที่กดรับแล้ว (อยู่ในตะกร้า รอส่งครัว) -->
      <div v-if="claims.length" class="border-top mt-2 pt-2">
        <div class="promo-section-title text-muted mb-1">{{ $t('_promotion.In cart') }}</div>
        <div
          v-for="c in claims"
          :key="c.promotion_item_id"
          class="d-flex align-items-center gap-2 bg-light rounded-3 px-2 py-2 mb-1"
          style="font-size: 0.82rem;"
        >
          <div class="reward-ic flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center" :class="c.is_pwp ? 'reward-ic--pwp' : 'reward-ic--gwp'">
            <i :class="c.is_pwp ? 'fa-solid fa-tag' : 'fa-solid fa-gift'" />
          </div>
          <div class="flex-grow-1 min-w-0 text-truncate">
            {{ $ln(c, 'reward_product_name') }}<span v-if="c.free_quantity > 1" class="text-muted"> × {{ c.free_quantity }}</span>
          </div>
          <span class="badge flex-shrink-0" :class="c.is_pwp ? 'bg-info-subtle text-info' : 'bg-success-subtle text-success'">
            {{ c.is_pwp ? $storeIndex.formatThaiCurrency(c.reward_price) : $t('_promotion.Free') }}
          </span>
          <button type="button" class="btn btn-sm btn-link text-danger p-0 flex-shrink-0" @click="removeClaim(c.promotion_item_id)">
            <i class="fa-solid fa-xmark" />
          </button>
        </div>
      </div>
    </div>

    <!-- config reward → เลือก variant/modifier ก่อนใส่ตะกร้า (reuse) -->
    <reward-select-modal
      v-if="configTier"
      ref="configModal"
      :reward-product-id="configTier.reward_product_id"
      :reward-name="$ln(configTier, 'reward_product_name')"
      :loading="false"
      @confirm="onConfigConfirm"
      @close="configTier = null"
    />
  </div>
</template>

<script lang="js">
import { ServicePromotion } from '@/services/endpoint/services.service';
import RewardSelectModal from '@/components/pos/loyalty/RewardSelectModal.vue';

export default {
  name: 'ServicePromotionPanel',
  components: { RewardSelectModal },
  props: {
    // ยอดตะกร้า (ยังไม่ส่งครัว) — ใช้คำนวณ progress สด ฝั่ง FE
    cartTotal: { type: Number, default: 0 },
    // โปรที่มากับ response หน้าหลัก (bill/menu) — panel ใช้แทนการ fetch เอง (ยิงเส้นเดียว)
    initialData: { type: Object, default: null },
    // false = parent คุมข้อมูลเอง (ส่ง initialData มา) → panel ไม่ fetch ตอน mount แม้ initialData ยังมาไม่ถึง
    // (parent fetch async → ตอน mount ค่ายัง null → รอ watcher applyData แทน กัน GET ซ้ำ)
    autoFetch: { type: Boolean, default: true }
  },
  emits: ['claims-changed'],
  data() {
    return {
      visible: false,
      loading: false,
      busy: false,
      promotionName: '',
      tierMode: 'single',
      showStockEarly: true,
      tiers: [],
      claimedTierIds: [],
      claims: [],       // reward ที่กดรับแล้ว รอส่งครัวพร้อมออร์เดอร์
      configTier: null  // tier ที่รอเลือก variant (config)
    };
  },
  computed: {
    // single mode: ใช้สิทธิ์ไปแล้ว (มี reward ในตะกร้าหรือรับบนบิลแล้ว) → ปิดการรับเพิ่ม
    promoUsed() {
      return this.tierMode === 'single' && (this.claims.length > 0 || this.claimedTierIds.length > 0);
    },
    // ใช้สิทธิ์แล้ว → ซ่อน progress ขั้นอื่น เหลือเฉพาะขั้นที่รับ/เลือกไว้ (กัน UI ขึ้น "อีก X" หลอกๆ)
    visibleTiers() {
      if (!this.promoUsed) return this.tiers;
      return this.tiers.filter((t) => this.isClaimedServer(t) || this.isPending(t));
    }
  },
  watch: {
    // parent re-fetch (เช่นหลังกดรับ → TabBill.fetchData) ส่ง promotion ใหม่ลงมา → apply (ไม่ fetch แยก)
    initialData(nv) {
      if (nv) this.applyData(nv);
    },
    cartTotal(nv) {
      // ส่งครัว/ล้างตะกร้า → ยอด 0: เคลียร์ claims เงียบๆ + refresh สถานะจาก server
      if (Number(nv) === 0) {
        if (this.claims.length) { this.claims = []; this.emitClaims(); }
        this.fetch();
        return;
      }
      // ลบสินค้าจนยอดต่ำกว่า tier → ถอด reward ที่กดไว้ + แจ้งลูกค้า
      const removed = [];
      this.claims = this.claims.filter((c) => {
        if (this.reachedByCart(c._tier)) return true;
        removed.push(c);
        return false;
      });
      if (removed.length) {
        removed.forEach((c) => this.$toast.warn(this.$t('_promotion.Cannot claim anymore') + ': ' + this.$ln(c, 'reward_product_name')));
        this.emitClaims();
      }
    }
  },
  mounted() {
    // มีข้อมูล bundled จาก parent → ใช้เลย; ยังไม่มี + autoFetch=false (parent คุม) → รอ watcher applyData
    // (กัน GET ซ้ำ: parent fetch async → ตอน mount ค่ายัง null แต่กำลังจะมา); autoFetch=true → fetch เอง (เดิม)
    if (this.initialData) this.applyData(this.initialData);
    else if (this.autoFetch) this.fetch();
  },
  methods: {
    applyData(d) {
      const data = d || {};
      this.promotionName = data.promotion_name || '';
      this.tierMode = data.promotion_tier_mode || 'single';
      this.showStockEarly = data.promotion_show_stock_early !== false;
      this.tiers = data.tiers || [];
      this.claimedTierIds = (data.claimed_tier_ids || []).map(Number);
      this.visible = !!data.has_promotion;
    },
    async fetch() {
      this.loading = true;
      try {
        const res = await ServicePromotion.get(this.$route.params.token);
        this.applyData(res.data?.data ?? res.data ?? {});
      } catch (err) {
        this.visible = false;
      } finally {
        this.loading = false;
      }
    },
    isClaimedServer(tier) {
      return this.claimedTierIds.includes(Number(tier.promotion_item_id));
    },
    isPending(tier) {
      return this.claims.some((c) => Number(c.promotion_item_id) === Number(tier.promotion_item_id));
    },
    canClaim(tier) {
      if (!this.reachedByCart(tier) || !tier.reward_in_stock || this.isPending(tier) || this.isClaimedServer(tier)) {
        return false;
      }
      // single mode: เลือกรับได้ขั้นใดขั้นหนึ่งที่ถึง แต่ 1 บิล 1 reward (ใช้แล้วปิด)
      if (this.tierMode === 'single' && this.promoUsed) {
        return false;
      }
      return true;
    },
    // นับรวมตะกร้า (เฉพาะ scope ทั้งบิล + เกณฑ์บาท); อื่นๆ ใช้ค่าจากของที่ส่งแล้ว
    remainingFor(tier) {
      const sentBase = Number(tier.base) || 0;
      const cartContribution = (tier.qualifying_scope === 'bill' && tier.condition_type === 'spend')
        ? (Number(this.cartTotal) || 0)
        : 0;
      if (cartContribution === 0) return Math.max(0, Number(tier.remaining) || 0);
      return Math.max(0, Number(tier.threshold) - (sentBase + cartContribution));
    },
    reachedByCart(tier) {
      return this.remainingFor(tier) <= 0;
    },
    // ---------- display helpers (progress bar สดตามตะกร้า) ----------
    tierProgress(tier) {
      const threshold = parseFloat(tier.threshold) || 0;
      if (threshold <= 0) return 100;
      const effectiveBase = Math.max(0, threshold - this.remainingFor(tier));
      return Math.min(100, Math.round((effectiveBase / threshold) * 100));
    },
    thresholdLabel(tier) {
      return tier.condition_type === 'quantity'
        ? (parseFloat(tier.threshold) || 0) + ' ' + this.$t('_promotion.pcs')
        : this.$storeIndex.formatThaiCurrency(tier.threshold);
    },
    baseLabel(tier) {
      const effectiveBase = Math.max(0, (parseFloat(tier.threshold) || 0) - this.remainingFor(tier));
      return tier.condition_type === 'quantity'
        ? effectiveBase + ' ' + this.$t('_promotion.pcs')
        : this.$storeIndex.formatThaiCurrency(effectiveBase);
    },
    remainingLabel(tier) {
      return tier.condition_type === 'quantity'
        ? this.remainingFor(tier) + ' ' + this.$t('_promotion.pcs')
        : this.$storeIndex.formatThaiCurrency(this.remainingFor(tier));
    },
    onClaimClick(tier) {
      if (!tier.reward_in_stock || !this.canClaim(tier)) return;
      // config → เลือก variant ก่อน
      if (tier.reward_is_config) {
        this.configTier = tier;
        return;
      }
      this.routeClaim(tier, {});
    },
    onConfigConfirm(selection) {
      if (this.configTier) this.routeClaim(this.configTier, selection || {});
      if (this.$refs.configModal) this.$refs.configModal.close();
      else this.configTier = null;
    },
    // ถึงจากของที่ "ส่งครัวแล้ว" (is_met) → รับเลย (BE สร้าง reward ทันที ไม่ต้องสั่งใหม่)
    // ถึงจาก "ตะกร้า" ที่ยังไม่ส่ง → ใส่ตะกร้า ไปส่งพร้อมออร์เดอร์
    routeClaim(tier, selection) {
      if (tier.is_met) {
        this.directClaim(tier, selection);
      } else {
        this.addClaim(tier, selection);
      }
    },
    async directClaim(tier, selection) {
      this.busy = true;
      try {
        const payload = { promotion_item_id: tier.promotion_item_id };
        if (selection.product_id) payload.product_id = selection.product_id;
        if (selection.modifier_option_ids && selection.modifier_option_ids.length) {
          payload.modifier_option_ids = selection.modifier_option_ids;
        }
        await ServicePromotion.claim(this.$route.params.token, payload);
        this.$toast.success(this.$t('_promotion.Reward added to bill'));
        await this.fetch();
        this.$emit('changed');
      } catch (err) {
        this.$toast.warn(err?.response?.data?.message || this.$t('_promotion.Failed to claim reward'));
      } finally {
        this.busy = false;
      }
    },
    addClaim(tier, selection) {
      if (this.isPending(tier)) return;
      this.claims.push({
        promotion_item_id: tier.promotion_item_id,
        reward_product_name: tier.reward_product_name,
        reward_product_name_en: tier.reward_product_name_en,
        is_pwp: tier.is_pwp,
        reward_price: tier.reward_price,
        free_quantity: tier.free_quantity,
        product_id: selection.product_id || null,
        modifier_option_ids: selection.modifier_option_ids || [],
        _tier: tier
      });
      this.emitClaims();
      this.$toast.success(this.$t('_promotion.Added reward to cart'));
    },
    removeClaim(promotionItemId) {
      this.claims = this.claims.filter((c) => Number(c.promotion_item_id) !== Number(promotionItemId));
      this.emitClaims();
    },
    emitClaims() {
      this.$emit('claims-changed', this.claims.map((c) => ({
        promotion_item_id: c.promotion_item_id,
        product_id: c.product_id || undefined,
        modifier_option_ids: c.modifier_option_ids || []
      })));
    }
  }
};
</script>

<style scoped>
.promo-icon {
  width: 34px;
  height: 34px;
  background: #d1e7dd;
  color: #198754;
  font-size: 15px;
}
.promo-used {
  background: #d1e7dd;
  color: #0f5132;
}
.promo-tier {
  border: 1px solid #e9ecef;
  background: #fff;
  transition: background-color .15s ease, border-color .15s ease;
}
.promo-tier--met {
  background: #f2fbf6;
  border-color: #badbcc;
}
.promo-section-title {
  font-size: 0.66rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .4px;
}
.reward-ic {
  width: 26px;
  height: 26px;
  font-size: 11px;
}
.reward-ic--gwp { background: #d1e7dd; color: #198754; }
.reward-ic--pwp { background: #cff4fc; color: #0aa2c0; }
.progress { background-color: #e9ecef; border-radius: 99px; }
.progress-bar { border-radius: 99px; transition: width .35s ease; }
</style>
